import type { AxiosResponse } from "axios";

import { Api } from "@penn/web-common";

// =============================================================================
// Quotation Phase C — document downloads (svc-06, /v1/tasks/...).
// Mirrors packages/shared-types/src/svc-06-quotation/v1/document.schema.ts.
//
// Cost Analysis (.xlsx) gate canGenerateRoi — STREAMED (ephemeral)
// ROI Report (.pdf) gate canGenerateRoi — STREAMED (ephemeral)
// Quotation (.pdf) gate canGenerateQuote — task-bound PERSISTS to S3
// Word (.docx) gate canGenerateQuote — task-bound PERSISTS to S3
// Ad-hoc quotation preview (.pdf) — STREAMED, never persisted
//
// Decimal money/qty travel as strings (rule §D.12).
// =============================================================================

export type DocumentFormat = "pdf" | "docx";
export type DocumentLanguage = "en" | "de";

export interface DocFastenerInput {
  brand: string;
  type?: string | null;
  qtyPerPart?: string;
  qtyPerPartA?: string;
  qtyPerPartB?: string;
}

export interface DocApplicationInput {
  applicationId?: string;
  name?: string | null;
  applicationNumber?: string | null;
  annualVolume?: string;
  batchSize?: string;
  fasteners: DocFastenerInput[];
}

export interface DocAssumptions {
  laborRate?: string;
  daysPerYear?: string;
  hoursPerDay?: string;
  scrapManual?: string;
  scrapAuto?: string;
  reworkCost?: string;
  oeeManual?: string;
  oeeAuto?: string;
  usefulLifeYears?: string;
}

export interface DocSolution {
  productId: string;
  name?: string | null;
  machineType?: string | null;
}

export interface DocSelectedOption {
  partNo: string;
  description?: string | null;
  category?: string | null;
  toolingType?: string | null;
  machineType?: string | null;
  itemName?: string | null;
  partName?: string | null;
  price?: string | null;
  currency?: string | null;
  requirements?: string | null;
  qty?: number | null;
}

export interface DocManualLineItem {
  description: string;
  price: string;
  qty: number;
  currency: string;
  partNo?: string | null;
  category?: string | null;
  itemName?: string | null;
}

export interface DocCustomer {
  companyName?: string | null;
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  companyAddress?: string | null;
}

// --- Request bodies ---------------------------------------------------------

export interface CostAnalysisRequest {
  regionCode: string;
  machineCostMode?: "shared" | "dedicated";
  machineTypes: string[];
  applications: DocApplicationInput[];
  assumptions?: DocAssumptions;
  /**
   * CR-006: per-machine configured BOM price (keyed by machineType). Sent when a
   * solution was manually configured so Cost Analysis reflects it instead of the
   * catalog default — mirrors the ROI report's per-assignment override.
   */
  machinePriceOverrideByMachineType?: Record<string, string>;
}

export interface CostAnalysisTaskRequest {
  compareMachineTypes?: string[];
  assumptions?: DocAssumptions;
}

/**
 * POC-parity ROI machine assignment (`buildRoiReportData`). One
 * entry per machine that has ≥1 application assigned on the comparison cards:
 * the machine is costed with ONLY its `applicationIds` and priced with the
 * configured BOM `machinePriceOverride` (the per-unit price the cards resolved,
 * = `costByProductId[productId].data.aggregate.investmentCost`).
 */
export interface RoiMachineAssignment {
  productId: string;
  applicationIds: string[];
  machinePriceOverride?: string;
}

export interface RoiReportRequest extends CostAnalysisRequest {
  baselineMachineType?: string | null;
  /**
   * Per-machine application assignment + configured price. When present, svc-06
   * builds the ROI per assigned machine (POC parity); when omitted it falls back
   * to costing every `machineTypes` entry against all `applications`.
   */
  machineAssignments?: RoiMachineAssignment[];
}

export interface RoiReportTaskRequest {
  baselineMachineType?: string | null;
  assumptions?: DocAssumptions;
}

export interface QuoteRenderRequest {
  format?: DocumentFormat;
  language?: DocumentLanguage;
  includeRoi?: boolean;
}

export interface AdHocQuoteRenderRequest {
  /** "pdf" (Download quotation) or "docx" (Download Word). Defaults to pdf. */
  format?: DocumentFormat;
  language?: DocumentLanguage;
  includeRoi?: boolean;
  regionCode: string;
  machineCostMode?: "shared" | "dedicated";
  solution: DocSolution;
  applications: DocApplicationInput[];
  selectedOptions?: DocSelectedOption[];
  manualLineItems?: DocManualLineItem[];
  assumptions?: DocAssumptions;
  customer?: DocCustomer;
}

// ---------------------------------------------------------------------------
// Browser download helpers
// ---------------------------------------------------------------------------

function filenameFromDisposition(
  disposition: string | undefined,
  fallback: string,
): string {
  if (!disposition) return fallback;
  // RFC 5987 (filename*=) then plain filename=
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disposition);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].replace(/^"|"$/g, ""));
    } catch {
      /* fall through */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  if (plain?.[1]) return plain[1];
  return fallback;
}

/** Save an axios blob response as a file, naming it from Content-Disposition. */
function saveBlobResponse(res: AxiosResponse<Blob>, fallbackName: string): void {
  const disposition =
    (res.headers?.["content-disposition"] as string | undefined) ?? undefined;
  const fileName = filenameFromDisposition(disposition, fallbackName);
  triggerBrowserDownload(res.data, fileName);
}

/** Trigger a browser "Save As" for a Blob. */
export function triggerBrowserDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick so the click has a chance to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ---------------------------------------------------------------------------
// 1. Cost Analysis (.xls) — streamed. POC parity: the server emits an
// HTML-as-.xls payload (application/vnd.ms-excel), so the download fallback
// name uses.xls (the real name still comes from Content-Disposition).
// ---------------------------------------------------------------------------

export async function downloadCostAnalysisForTask(
  taskId: string,
  body: CostAnalysisTaskRequest = {},
): Promise<void> {
  const res = await Api.post<Blob>(
    `/v1/tasks/${encodeURIComponent(taskId)}/cost-analysis`,
    body,
    { responseType: "blob" },
  );
  saveBlobResponse(res, `cost-analysis-${taskId}.xls`);
}

export async function downloadCostAnalysisAdHoc(
  body: CostAnalysisRequest,
): Promise<void> {
  const res = await Api.post<Blob>(`/v1/tasks/cost-analysis`, body, {
    responseType: "blob",
  });
  saveBlobResponse(res, `cost-analysis.xls`);
}

// ---------------------------------------------------------------------------
// 2. ROI Report (.pdf) — streamed
// ---------------------------------------------------------------------------

export async function downloadRoiReportForTask(
  taskId: string,
  body: RoiReportTaskRequest = {},
): Promise<void> {
  const res = await Api.post<Blob>(
    `/v1/tasks/${encodeURIComponent(taskId)}/roi-report`,
    body,
    { responseType: "blob" },
  );
  saveBlobResponse(res, `roi-report-${taskId}.pdf`);
}

export async function downloadRoiReportAdHoc(body: RoiReportRequest): Promise<void> {
  const res = await Api.post<Blob>(`/v1/tasks/roi-report`, body, {
    responseType: "blob",
  });
  saveBlobResponse(res, `roi-report.pdf`);
}

// ---------------------------------------------------------------------------
// 3 + 4. Quotation render (.pdf /.docx) — streamed (LD-3 dropped, never persisted)
// ---------------------------------------------------------------------------

/** Task-bound quotation download — streamed bytes. Approval-gated (LD-6): throws
 * 409 awaiting-approval until the flow is complete. */
export async function downloadQuoteForTask(
  taskId: string,
  body: QuoteRenderRequest = {},
): Promise<void> {
  const res = await Api.post<Blob>(
    `/v1/tasks/${encodeURIComponent(taskId)}/quote/render`,
    body,
    { responseType: "blob" },
  );
  const ext = body.format === "docx" ? "docx" : "pdf";
  saveBlobResponse(res, `quotation-${taskId}.${ext}`);
}

/** Ad-hoc quotation preview — PDF or Word (POC parity), never persisted. */
export async function downloadQuoteAdHoc(
  body: AdHocQuoteRenderRequest,
): Promise<void> {
  const res = await Api.post<Blob>(`/v1/tasks/quote/render`, body, {
    responseType: "blob",
  });
  const ext = body.format === "docx" ? "docx" : "pdf";
  saveBlobResponse(res, `quotation.${ext}`);
}

// ---------------------------------------------------------------------------
// 5. Support response (.pdf/.docx) — CR-005 message-only support document. The
// PDF header carries the PennEngineering logo (as per the quotation PDF).
// ---------------------------------------------------------------------------

/** Support-response document for a message-only support task (CR-005). */
export async function downloadSupportResponseForTask(
  taskId: string,
  format: DocumentFormat,
): Promise<void> {
  const res = await Api.post<Blob>(
    `/v1/tasks/${encodeURIComponent(taskId)}/support-response/render`,
    { format },
    { responseType: "blob" },
  );
  saveBlobResponse(res, `support-response-${taskId}.${format}`);
}
